import { bookshelfGroups, defaultBookshelfGroupId } from "@shared/bookshelf";
import { Grid2X2, List, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@web/components/ui/button";
import { Input } from "@web/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@web/components/ui/select";
import type { BookshelfViewItem, SearchResult } from "../api";
import {
  filterBookshelfByGroup,
  filterBookshelfByKeyword,
  getBookshelfLatestChapterLabel,
  getBookshelfUnreadCount,
} from "../reader/reader-library";
import { ShelfBookGridItem, ShelfBookListItem } from "@web/components/features/shelf/book-card";
import type { ReaderWorkspace } from "../reader/reader-workspace";
import { EmptyState } from "@web/components/features/common/reader-widgets";
import { SearchResultDetailDialog } from "../search/search-result-detail-dialog";
import { createSearchResultFromBookshelfItem } from "../search/search-result-detail";

type ShelfViewMode = "item" | "grid";

export function ShelfPage({ workspace }: { workspace: ReaderWorkspace }) {
  const [selectedGroupId, setSelectedGroupId] = useState(defaultBookshelfGroupId);
  const [keyword, setKeyword] = useState("");
  const [viewMode, setViewMode] = useState<ShelfViewMode>("item");
  const [menuResult, setMenuResult] = useState<SearchResult | null>(null);

  const selectedGroup = bookshelfGroups.find(group => group.id === selectedGroupId) ?? bookshelfGroups[0]!;
  const groupedBooks = useMemo(() => filterBookshelfByGroup(workspace.bookshelf, selectedGroup.id), [workspace.bookshelf, selectedGroup.id]);
  const visibleBookshelf = useMemo(() => filterBookshelfByKeyword(groupedBooks, keyword), [groupedBooks, keyword]);

  return (
    <div className="space-y-3">
      <ShelfToolbar
        bookCount={groupedBooks.length}
        keyword={keyword}
        selectedGroupId={selectedGroup.id}
        viewMode={viewMode}
        onGroupChange={setSelectedGroupId}
        onKeywordChange={setKeyword}
        onViewModeChange={setViewMode}
      />

      <ShelfBookCollection
        items={visibleBookshelf}
        selectedSourceByBook={workspace.selectedSourceByBook}
        viewMode={viewMode}
        isBookshelfEmpty={workspace.bookshelf.length === 0}
        onOpen={item => void workspace.handleOpen(item)}
        onOpenMenu={item => setMenuResult(createSearchResultFromBookshelfItem(item))}
      />

      {menuResult && (
        <SearchResultDetailDialog
          result={menuResult}
          workspace={workspace}
          onClose={() => setMenuResult(null)}
        />
      )}
    </div>
  );
}

function ShelfToolbar({
  bookCount,
  keyword,
  selectedGroupId,
  viewMode,
  onGroupChange,
  onKeywordChange,
  onViewModeChange,
}: {
  bookCount: number;
  keyword: string;
  selectedGroupId: string;
  viewMode: ShelfViewMode;
  onGroupChange: (groupId: string) => void;
  onKeywordChange: (keyword: string) => void;
  onViewModeChange: (mode: ShelfViewMode) => void;
}) {
  const selectedGroup = bookshelfGroups.find(group => group.id === selectedGroupId) ?? bookshelfGroups[0]!;

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <ShelfGroupSelect selectedGroupId={selectedGroup.id} bookCount={bookCount} onGroupChange={onGroupChange} />
        <ShelfViewModeSwitch viewMode={viewMode} onViewModeChange={onViewModeChange} />
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={keyword} onChange={event => onKeywordChange(event.target.value)} placeholder="搜索书架里的书" className="pl-9" />
      </div>
    </section>
  );
}

function ShelfGroupSelect({
  selectedGroupId,
  bookCount,
  onGroupChange,
}: {
  selectedGroupId: string;
  bookCount: number;
  onGroupChange: (groupId: string) => void;
}) {
  const selectedGroup = bookshelfGroups.find(group => group.id === selectedGroupId) ?? bookshelfGroups[0]!;

  return (
    <Select value={selectedGroup.id} onValueChange={onGroupChange}>
      <SelectTrigger className="w-[9.5rem] shrink-0 justify-between text-left text-base font-semibold shadow-none sm:w-[11rem]">
        <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
          <span className="truncate">{selectedGroup.label}</span>
          <span className="shrink-0 text-[11px] font-normal text-muted-foreground">{bookCount} 本</span>
        </span>
      </SelectTrigger>
      <SelectContent align="start">
        {bookshelfGroups.map(group => (
          <SelectItem key={group.id} value={group.id}>
            {group.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ShelfViewModeSwitch({
  viewMode,
  onViewModeChange,
}: {
  viewMode: ShelfViewMode;
  onViewModeChange: (mode: ShelfViewMode) => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-1 rounded-md border p-1">
      <Button
        type="button"
        size="sm"
        aria-label="列表模式"
        title="列表模式"
        variant={viewMode === "item" ? "secondary" : "ghost"}
        onClick={() => onViewModeChange("item")}
      >
        <List className="size-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        aria-label="网格模式"
        title="网格模式"
        variant={viewMode === "grid" ? "secondary" : "ghost"}
        onClick={() => onViewModeChange("grid")}
      >
        <Grid2X2 className="size-4" />
      </Button>
    </div>
  );
}

function ShelfBookCollection({
  items,
  selectedSourceByBook,
  viewMode,
  isBookshelfEmpty,
  onOpen,
  onOpenMenu,
}: {
  items: BookshelfViewItem[];
  selectedSourceByBook: Record<string, string>;
  viewMode: ShelfViewMode;
  isBookshelfEmpty: boolean;
  onOpen: (item: BookshelfViewItem) => void;
  onOpenMenu: (item: BookshelfViewItem) => void;
}) {
  if (isBookshelfEmpty) {
    return (
      <div className="border-t pt-3">
        <EmptyState text="书架还是空的，去搜索页添加一本书" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="border-t pt-3">
        <EmptyState text="当前分组里没有匹配的书" />
      </div>
    );
  }

  if (viewMode === "grid") {
    return (
      <div className="grid grid-cols-2 gap-2 border-t pt-3 sm:grid-cols-3 lg:grid-cols-5">
        {items.map(item => (
          <ShelfBookGridItem
            key={item.item.id}
            title={item.book.title}
            coverUrl={item.book.coverUrl}
            onOpen={() => onOpen(item)}
            onOpenMenu={() => onOpenMenu(item)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="border-t pt-3">
      {items.map(item => (
        <ShelfBookListItem
          key={item.item.id}
          title={item.book.title}
          author={item.book.author}
          coverUrl={item.book.coverUrl}
          lastReadLabel={item.progress ? `第 ${item.progress.chapterIndex + 1} 章` : "尚未开始"}
          latestChapterLabel={getBookshelfLatestChapterLabel(item, selectedSourceByBook)}
          unreadCount={getBookshelfUnreadCount(item, selectedSourceByBook)}
          onOpen={() => onOpen(item)}
          onOpenMenu={() => onOpenMenu(item)}
        />
      ))}
    </div>
  );
}
